<script lang="ts" setup>
  import { VPhoneInput } from 'v-phone-input'
  import { useI18n } from 'vue-i18n'
  import { definePage } from 'unplugin-vue-router/runtime'
  import type { User } from '@/types/User.ts'
  import DateInput from '@/components/DateInput.vue'
  import { useAuthStore } from '@stores/auth.ts'
  import { AxiosError, type AxiosResponse } from 'axios'

  const { t } = useI18n()

  definePage({
    meta: {
      authenticated: true,
      title: 'update_profile',
      layout: 'default',
    },
  })

  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)
  const form = authStore.getUpdateProfileForm()
  const uploadForm = useForm('post', 'users/profile/picture', {
    file: null,
  })
  const snackbar = ref(false)
  const snackbarText = ref('')
  const snackbarColor = ref('success')
  const avatarUrl = ref(user.value?.avatar_media?.url)
  const fileInputRef = ref<HTMLInputElement | null>(null)

  const submit = async () => {
    try {
      const result = await form.submit()
      const { data } = result
      authStore.user = data
      snackbarColor.value = 'success'
      snackbarText.value = t('updated_successfully')
      snackbar.value = true
      form.setErrors({})
      form.hasErrors = false
      form.errors = {}
    } catch (e) {
      console.error(e)
    }
  }

  const onFileChange = async () => {
    uploadForm.validateFiles()

    if (uploadForm.invalid('file')) {
      return
    }

    try {
      const { data } = await uploadForm.submit({
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }) as AxiosResponse<User>
      avatarUrl.value = data?.avatar_media?.url
      snackbarColor.value = 'success'
      snackbarText.value = t('profile_picture_updated_successfully')
      snackbar.value = true
      uploadForm.file = null
    } catch (error) {
      console.error(error)
      if (!(error instanceof AxiosError)) {
        snackbarText.value = t('something_went_wrong')
      } else {
        snackbarText.value = error?.response?.data?.message || t('something_went_wrong')
      }

      snackbarColor.value = 'error'
      snackbar.value = true
      uploadForm.file = null
    }
  }

</script>

<template>
  <v-container class="py-0" :fluid="$vuetify.display.smAndDown">
    <v-card class="mx-auto py-7 px-4" elevation="0" max-width="540">
      <v-card-title class="font-weight-bold text-h6 pa-0 mb-8">
        <div class="d-flex align-center ga-3 text-gray-100">
          <v-btn
            class="p-0 m-0"
            icon="mdi-arrow-right"
            :ripple="false"
            size="32"
            :to="{name:'root'}"
            variant="text"
          />
          <i18n-t keypath="update_profile" />
        </div>
      </v-card-title>
      <v-row class="mb-5" justify="center">
        <v-col cols="auto">
          <div class="position-relative">
            <v-avatar border="0" color="grey-600" size="80">
              <v-img v-if="avatarUrl" :src="avatarUrl" />
              <v-icon v-else border="border-0" color="white" size="52">mdi-account-outline</v-icon>
            </v-avatar>
            <v-btn
              color="background"
              icon="mdi-camera"
              size="x-small"
              style="inset-inline-start:-30px; bottom:-30px"
              @click="fileInputRef?.click()"
            />
          </div>
          <v-file-input
            ref="fileInputRef"
            v-model="uploadForm.file"
            accept="image/jpg,image/png,image/jpeg"
            :error="uploadForm.invalid('file')"
            :error-messages="uploadForm.errors.file"
            hide-input
            prepend-icon=""
            @update:model-value="onFileChange"
          />
        </v-col>
      </v-row>

      <v-form @submit.prevent="submit">
        <v-row>
          <v-col class="py-0" cols="12">
            <div class="text-gray-100 text-body-1 font-weight-bold mb-2">
              <i18n-t keypath="first_name" />
            </div>
            <v-text-field
              v-model="form.first_name"
              :class="{'outlined-primary-empty': !form.first_name}"
              density="compact"
              :error="form.invalid('first_name')"
              :error-messages="form.errors.first_name"
              :placeholder="t('first_name')"
              variant="outlined"
              @click:clear="form.validate('first_name')"
            />
          </v-col>
          <v-col class="py-0" cols="12">
            <div class="text-gray-100 text-body-1 font-weight-bold mb-2">
              <i18n-t keypath="last_name" />
            </div>
            <v-text-field
              v-model="form.last_name"
              :class="{'outlined-primary-empty': !form.last_name}"
              density="compact"
              :error="form.invalid('last_name')"
              :error-messages="form.errors.last_name"
              :placeholder="t('last_name')"
              variant="outlined"
              @click:clear="form.validate('last_name')"
            />
          </v-col>

          <v-col class="py-0" cols="12">
            <div class="text-gray-100 text-body-1 font-weight-bold mb-2">
              <i18n-t keypath="birthdate" />
            </div>
            <date-input
              v-model="form.birthdate"
              append-inner-icon="$calendar"
              :class="{'outlined-primary-empty': !form.birthdate}"
              clearable
              density="compact"
              :error="form.invalid('birthdate')"
              :error-messages="form.errors.birthdate"
              :first-day-of-week="0"
              :placeholder="t('birthdate')"
              prepend-icon=""
              variant="outlined"
              @click:clear="form.validate('birthdate')"
            />
          </v-col>

          <v-col class="py-0" cols="12">
            <div class="text-gray-100 text-body-1 font-weight-bold mb-2">
              <i18n-t keypath="gender" />
            </div>
            <v-radio-group
              v-model="form.gender"
              :class="{'outlined-primary-empty': !form.gender}"
              color="primary"
              density="compact"
              :error="form.invalid('gender')"
              :error-messages="form.errors.gender"
              inline
              :placeholder="t('gender')"
              variant="outlined"
              @click:clear="form.validate('gender')"
            >
              <div class="d-flex ga-2">
                <v-radio class="font-weight-bold" :label="t('male')" value="male" />
                <v-radio class="font-weight-bold" :label="t('female')" value="female" />
              </div>
            </v-radio-group>
          </v-col>
          <v-col class="py-0" cols="12">
            <div class="text-gray-100 text-body-1 font-weight-bold mb-2">
              <i18n-t keypath="phone" />
            </div>
            <v-locale-provider locale="en">
              <v-phone-input
                v-model="form.phone"
                :class="{'outlined-primary-empty': !form.phone}"
                density="compact"
                enable-searching-country
                :error="form.invalid('phone')"
                :error-messages="form.errors.phone"
                :invalid-message="''"
                :placeholder="t('phone')"
                variant="outlined"
                @click:clear="form.validate('phone')"
              />
            </v-locale-provider>
          </v-col>

          <v-col class="py-0" cols="12">
            <div class="text-gray-100 text-body-1 font-weight-bold mb-2">
              <i18n-t keypath="email" />
            </div>
            <v-text-field
              v-model="form.email"
              :class="{'outlined-primary-empty': !form.email}"
              density="compact"
              :error="form.invalid('email')"
              :error-messages="form.errors.email"
              :placeholder="t('email')"
              type="email"
              variant="outlined"
              @click:clear="form.validate('email')"
            />
          </v-col>
          <v-col cols="12">
            <v-btn
              block
              class="text-subtitle-1 font-weight-bold"
              color="primary"
              :disabled="form.processing || form.validating"
              size="large"
              style="font-weight: 500"
              type="submit"
              variant="flat"
            >
              <i18n-t keypath="save_changes" />
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
    <v-snackbar v-model="snackbar" close-delay="5" close-on-content-click :color="snackbarColor">
      <div class="text-center">{{ snackbarText }}</div>
    </v-snackbar>
  </v-container>
</template>

<style lang="scss" scoped>
.outlined-primary-empty {
  :deep(.v-field__outline) {
    color: rgb(var(--v-theme-primary));
    --v-field-border-opacity: 1;
  }

  :deep(.v-selection-control-group) {
    border: 1px solid rgb(var(--v-theme-primary));
    border-radius: 4px;
    padding: 10px 0;
  }
}
</style>
